import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import UserControls from 'flarum/forum/utils/UserControls';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import Switch from 'flarum/common/components/Switch';
import FieldSet from 'flarum/common/components/FieldSet';
import Tooltip from 'flarum/common/components/Tooltip';
import ChallengeModal from './components/ChallengeModal';
import TokenAdjustModal from './components/TokenAdjustModal';
import { getContrastColor } from '../common';
import { battlePassConfig, battlePassLevelFor } from './battlePass';
/** Arena stats badge (wins count + tooltip with full stats). */
function arenaBadge(user, extraClass) {
  if (extraClass === void 0) {
    extraClass = '';
  }
  if (app.forum.attribute('arenaBadgeEnabled') === false) return null;
  const stats = user.attribute('arenaStats');
  if (!stats) return null;
  const wins = stats.wins ?? 0;
  const losses = stats.losses ?? 0;
  const draws = stats.draws ?? 0;
  const color = app.forum.attribute('arenaBadgeColor') || '#80b77e';
  const icon = app.forum.attribute('arenaBadgeIcon') || 'fas fa-dungeon';
  return m(Tooltip, {
    text: app.translator.trans('forumaker-arena.forum.badge.tooltip', {
      wins: String(wins),
      losses: String(losses),
      draws: String(draws)
    }),
    position: "bottom"
  }, m("span", {
    className: `Badge ArenaWinBadge ${extraClass}`,
    style: {
      background: color,
      color: getContrastColor(color)
    }
  }, m("i", {
    className: icon
  }), m("span", {
    className: "ArenaWinBadge-count"
  }, wins)));
}
export default function addArenaUserExtensions() {
  // 1. "Challenge" item in the UserCard-controls dropdown menu
  extend(UserControls, 'userControls', function (items, user) {
    if (!app.session.user) return;
    if (app.session.user.id() === user.id()) return;
    if (!user.attribute('arenaStats')) return;
    items.add('challengeArena', m(Button, {
      icon: "fas fa-swords",
      onclick: () => app.modal.show(ChallengeModal, {
        opponent: user
      })
    }, app.translator.trans('forumaker-arena.forum.challenge.btn')), 50);
  });

  // 1b. "Жетоны" item — manual grant/deduct, gated by arena.manageTokens
  // (see ForumResourceFields' canManageTokens). Lives in moderationControls
  // alongside the other moderation actions (Очки/points, edit, audit, …)
  // rather than its own userControls group, priority 84 so it sits right
  // after ramon/point-system's "Очки" button (priority 85).
  extend(UserControls, 'moderationControls', function (items, user) {
    if (app.forum.attribute('canManageTokens') !== true) return;
    items.add('arenaTokens', m(Button, {
      icon: "fas fa-coins",
      onclick: () => app.modal.show(TokenAdjustModal, {
        user
      })
    }, app.translator.trans('forumaker-arena.forum.token_adjust.btn')), 84);
  });

  // 2. Level plaque in UserCard's infoItems (profile): a translucent pill
  // with the Arena icon, «N уровень» (Battle Pass level) and the wins count
  // in a small bubble. Links to the user's Arena tab.
  extend('flarum/forum/components/UserCard', 'infoItems', function (items) {
    const user = this.attrs?.user;
    if (!user) return;

    // Public attribute, see UserResourceFields
    if (user.attribute('arenaShowWins') === false) return;
    if (app.forum.attribute('arenaBadgeEnabled') === false) return;
    const stats = user.attribute('arenaStats');
    if (!stats) return;
    const wins = stats.wins ?? 0;
    const config = battlePassConfig();
    const icon = app.forum.attribute('arenaBadgeIcon') || 'fas fa-dungeon';
    items.add('arenaWins', m(Tooltip, {
      text: app.translator.trans('forumaker-arena.forum.badge.tooltip', {
        wins: String(wins),
        losses: String(stats.losses ?? 0),
        draws: String(stats.draws ?? 0)
      }),
      position: "bottom"
    }, m(Link, {
      href: app.route('user.arena', {
        username: user.username()
      }),
      className: "ArenaLevelBadge"
    }, m("i", {
      className: icon
    }), config.configured ?
    // Battle Pass on: «N уровень» + wins in the small bubble.
    m('[', null, m("span", {
      className: "ArenaLevelBadge-level"
    }, app.translator.trans('forumaker-arena.forum.badge.level', {
      level: String(battlePassLevelFor(user, config))
    })), m("span", {
      className: "ArenaLevelBadge-wins"
    }, wins)) :
    // Battle Pass off: no level to show — just the wins count, like
    // the pre-pass badge but in the new plaque style.
    m("span", {
      className: "ArenaLevelBadge-level"
    }, wins))), -10);
  });

  // 3. Stats badge next to the avatar in posts
  // String-based extend: PostUser may live in a lazy chunk (see addCardSlider)
  extend('flarum/forum/components/PostUser', 'userViewItems', function (items, user) {
    if (!user) return;
    if (user.attribute('arenaShowStatsInPosts') === false) return;
    const badge = arenaBadge(user, 'ArenaWinBadge--post');
    if (badge) {
      items.add('arenaStats', m("span", {
        className: "ArenaPostBadge"
      }, badge), 85);
    }
  });

  // 4. "Arena" section in user settings
  extend('flarum/forum/components/SettingsPage', 'settingsItems', function (items) {
    const user = this.user;
    if (!user) return;
    const prefs = user.preferences?.() ?? {};
    const showWins = prefs['arenaShowWins'] !== false;
    const showStatsInPosts = prefs['arenaShowStatsInPosts'] !== false;
    items.add('arena', m(FieldSet, {
      className: "ArenaSettings",
      label: app.translator.trans('forumaker-arena.forum.settings.section_label')
    }, m(Switch, {
      state: showWins,
      onchange: value => user.savePreferences({
        arenaShowWins: value
      })
    }, app.translator.trans('forumaker-arena.forum.settings.show_stats_profile')), m(Switch, {
      state: showStatsInPosts,
      onchange: value => user.savePreferences({
        arenaShowStatsInPosts: value
      })
    }, app.translator.trans('forumaker-arena.forum.settings.show_stats_posts'))), -10);
  });
}